import React, { FunctionComponent, useCallback, useMemo } from 'react';

import dateToString from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { ArrowLeft } from '@yandex-360/iconostasis/dist/icons/ArrowLeft';
import { Link } from '@yandex-360/iconostasis/dist/icons/Link';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { NOTIFICATIONS_MAP } from '../../../../../../shared/features/notifications/ps-notifications';
import { i18n, i18nMonthsGenitive } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { getUrl, SCREENS } from '../../../../../../shared/helpers/navigation';
import { useActions } from '../../../../../../shared/hooks';
import { NotificationId } from '../../../../../call/types';
import { formatConfShortUrlId } from '../../../../helpers/format-conf-short-url-id';
import { formatFromTo } from '../../../../helpers/format-date';
import { getConferenceTitle } from '../../../../helpers/get-conference-title';
import { useIsOneColLayout } from '../../../../hooks';
import { selectSelectedSession } from '../../../../selectors';
import { ConferenceHistoryItem } from '../../../../types';
import { copyToCB, setSelectedSessionId } from '../../slice';
import { Artifact } from '../artifact';
import styles from './styles.css';

export const Header: FunctionComponent = () => {
    const session = useSelector(selectSelectedSession) as ConferenceHistoryItem;
    const { shortUrlId, startedAt, endedAt } = session;
    const formattedDateTime = useMemo(() => {
        const startDate = new Date(startedAt);
        const dateFormatted = dateToString(startDate, 'DD mmmm', [], i18nMonthsGenitive());
        return `${dateFormatted}, ${formatFromTo(startedAt, endedAt)}`;
    }, [endedAt, startedAt]);
    const confFormattedNumber = formatConfShortUrlId(shortUrlId);
    const actions = useActions({
        copyToCB,
        setSelectedSessionId,
    });
    const onCopyConfUrlClick = useCallback(() => {
        countMetrika(
            'meeting_history',
            'meeting_stat',
            'copy_meeting_link_c',
            shortUrlId,
            dateToString(new Date(startedAt), 'YYYY-MM-DD')
        );
        const confUrl = `${window.location.origin}${getUrl(SCREENS.CALL, { id: shortUrlId })}`;

        actions.copyToCB({
            value: confUrl,
            notificationItem: NOTIFICATIONS_MAP[NotificationId.LINK_COPIED_CONF_HISTORY](),
        });
    }, [shortUrlId, startedAt, actions]);
    const isOneColLayout = useIsOneColLayout();
    const onBackPress = useCallback(() => {
        actions.setSelectedSessionId(null);
    }, [actions]);
    return (
        <div className={cn(styles.header, { [styles.oneColLayout]: isOneColLayout })}>
            <div className={styles.titleAndDateTimeWrapper}>
                {isOneColLayout && (
                    <Button
                        onPress={onBackPress}
                        view="contrast-transparent"
                        icon={<ArrowLeft variant="outline" size="sm" />}
                    />
                )}
                <div className={styles.titleAndDateTime}>
                    <Text as="h2" overflow="ellipsis" typography="heading-sm" className={styles.title}>
                        {getConferenceTitle(session)}
                    </Text>
                    <Text typography="body-md-regular" className={styles.datetime} color="secondary">
                        {formattedDateTime}
                    </Text>
                </div>
            </div>
            <Artifact
                title={i18n('conferences-history', 'conf-number')}
                subTitle={confFormattedNumber}
                className={styles.confNumber}
            >
                <Button onClick={onCopyConfUrlClick} iconLeft={<Link variant="outline" size="sm" />}>
                    {i18n('call', 'meeting-link')}
                </Button>
            </Artifact>
        </div>
    );
};
